/**
 * Confirmation card — tóm tắt + nút Lưu/Sửa/Hủy.
 * HITL gate trước mọi write op.
 */
import { AlertTriangle, CheckCircle2, X } from "lucide-react";
import * as React from "react";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { cn } from "@/lib/utils";

interface SummaryRow {
  label: string;
  value: string;
  highlight?: boolean;
  warning?: string;
}

interface ConfirmationCardPayload {
  title: string;
  description?: string;
  summary: SummaryRow[];
  warnings?: string[];
  save_label?: string;
  edit_label?: string;
  cancel_label?: string;
  pending_action: { tool: string; args: Record<string, unknown> };
}

interface ConfirmationCardProps {
  payload: ConfirmationCardPayload;
  status?: "pending" | "submitted" | "approved" | "rejected";
  onApproval?: (
    action: "save" | "cancel" | "approve" | "reject" | "request_change",
  ) => void;
}

export function ConfirmationCard({
  payload,
  status = "pending",
  onApproval,
}: ConfirmationCardProps): JSX.Element {
  const isLocked = status !== "pending";

  return (
    <Card
      className={cn(
        "border-2 border-primary",
        status === "approved" && "border-success",
        status === "rejected" && "border-destructive opacity-70",
      )}
    >
      <CardHeader className="bg-primary-soft/50 rounded-t-lg">
        <CardTitle className="text-base flex items-center gap-2">
          {status === "approved" ? (
            <CheckCircle2 className="size-5 text-success" />
          ) : status === "rejected" ? (
            <X className="size-5 text-destructive" />
          ) : (
            <AlertTriangle className="size-5 text-primary" />
          )}
          {payload.title}
        </CardTitle>
        {payload.description ? (
          <p className="text-sm text-muted-foreground">{payload.description}</p>
        ) : null}
      </CardHeader>

      <CardContent className="space-y-2 pt-4">
        <dl className="grid grid-cols-[140px_1fr] gap-x-3 gap-y-1.5 text-sm">
          {payload.summary.map((row, idx) => (
            <React.Fragment key={`${row.label}-${idx}`}>
              <dt className="text-muted-foreground">{row.label}</dt>
              <dd
                className={cn(
                  "font-medium",
                  row.highlight && "text-warning text-base font-semibold",
                )}
              >
                {row.value}
                {row.warning ? (
                  <span className="ml-2 text-xs text-warning">⚠ {row.warning}</span>
                ) : null}
              </dd>
            </React.Fragment>
          ))}
        </dl>

        {payload.warnings && payload.warnings.length > 0 ? (
          <div className="mt-3 space-y-1.5">
            {payload.warnings.map((w, i) => (
              <div
                key={i}
                className="flex items-start gap-2 rounded-md bg-warning/10 border border-warning/30 p-2 text-xs"
              >
                <AlertTriangle className="size-3.5 mt-0.5 shrink-0 text-warning" />
                <span>{w}</span>
              </div>
            ))}
          </div>
        ) : null}
      </CardContent>

      <CardFooter className="border-t pt-3 gap-2">
        <Button
          variant="ghost"
          size="sm"
          disabled={isLocked}
          onClick={() => onApproval?.("cancel")}
        >
          {payload.cancel_label ?? "Hủy"}
        </Button>
        <Button
          variant="outline"
          size="sm"
          disabled={isLocked}
          onClick={() => onApproval?.("request_change")}
        >
          {payload.edit_label ?? "Sửa"}
        </Button>
        <Button
          variant="success"
          size="sm"
          disabled={isLocked}
          onClick={() => onApproval?.("save")}
        >
          ✅ {payload.save_label ?? "Lưu vào ERP"}
        </Button>
      </CardFooter>
    </Card>
  );
}
